import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/app/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input, Label } from "@/components/ui/form";
import { CalendarCheck2, Droplets, ShieldCheck, Sparkles } from "lucide-react";

export function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      nav("/", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-svh bg-white lg:grid lg:grid-cols-[minmax(0,1.05fr)_minmax(440px,0.95fr)]">
      <section className="surface-grid relative hidden overflow-hidden bg-slate-950 p-12 text-white lg:flex lg:flex-col">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(59,130,246,0.28),transparent_35%),radial-gradient(circle_at_80%_85%,rgba(14,165,233,0.18),transparent_35%)]" />
        <div className="relative flex items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-xl bg-white text-primary shadow-lg"><Droplets className="h-6 w-6" /></span>
          <div><p className="text-xl font-bold tracking-[-0.03em]">Shinebright</p><p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">Car care CRM</p></div>
        </div>
        <div className="relative my-auto max-w-xl">
          <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-blue-200"><Sparkles className="h-3.5 w-3.5" /> Built for modern car-care operations</p>
          <h1 className="text-5xl font-semibold leading-[1.08] tracking-[-0.045em]">One clear view of every customer, booking, and bay.</h1>
          <p className="mt-6 max-w-lg text-base leading-7 text-slate-300">Run the day with confidence. Coordinate your team, protect every slot, and keep the customer experience polished from booking to payment.</p>
          <div className="mt-10 grid grid-cols-2 gap-3">
            <Feature icon={CalendarCheck2} label="Conflict-safe scheduling" />
            <Feature icon={ShieldCheck} label="Secure operational records" />
          </div>
        </div>
        <p className="relative text-xs text-slate-500">Operations workspace · Authorized staff only</p>
      </section>

      <section className="flex min-h-svh items-center justify-center bg-background px-5 py-10 sm:px-10">
        <div className="w-full max-w-[420px]">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <span className="grid h-10 w-10 place-items-center rounded-xl bg-primary text-white"><Droplets className="h-5 w-5" /></span>
            <span className="text-lg font-bold tracking-tight">Shinebright</span>
          </div>
          <Card className="border-border/70 shadow-lift">
            <CardHeader className="pb-4">
              <p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-primary">Staff portal</p>
              <CardTitle className="text-2xl">Welcome back</CardTitle>
              <CardDescription>Sign in to continue to your operations workspace.</CardDescription>
            </CardHeader>
            <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            {error && (
              <div role="alert" className="rounded-lg border border-destructive/25 bg-destructive/5 px-3 py-2.5 text-sm text-destructive">
                {error}
              </div>
            )}
            <Button type="submit" disabled={submitting} className="mt-2 h-11 w-full">
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>
            </CardContent>
          </Card>
          <p className="mt-5 text-center text-xs text-muted-foreground">Protected by secure sessions and role-based access.</p>
        </div>
      </section>
    </div>
  );
}

function Feature({ icon: Icon, label }: { icon: typeof ShieldCheck; label: string }) {
  return <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.045] px-4 py-3 text-sm text-slate-200"><span className="grid h-8 w-8 place-items-center rounded-lg bg-blue-500/15 text-blue-300"><Icon className="h-4 w-4" /></span>{label}</div>;
}
